import { useEffect, useState } from 'react';

import { CheckCircle2, Eye, EyeOff, RefreshCw } from 'lucide-react';
import Badge from 'shared/ui/badge';
import PhoneInput, { isValidPhoneForCountry, parsePhoneDigits } from 'shared/ui/phone-input';
import { extractErrorMessage } from 'shared/utils/extractErrorMessage';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import {
  useConfigureD360ChannelMutation,
  useConfigureD360WebhookMutation,
  useGetProjectPhoneConfigQuery,
  useLazyLookupD360ChannelQuery,
  useLazyLookupProjectByPhoneQuery,
  useRefreshProjectWhatsAppHealthMutation,
  useUpdateProjectMutation,
  useUpdateProjectPhoneMutation,
} from '../api/master.api';
import type { ProjectDetail, WhatsAppMessagingLimitTier, WhatsAppQualityRating } from '../api/types';
import ConfigureNumberDialog from './ConfigureNumberDialog';

interface Props {
  project: ProjectDetail;
}

interface Draft {
  phoneNumber: string;
  d360ChannelId: string;
  d360ApiKey: string;
}

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'default';

const QUALITY_LABELS: Record<WhatsAppQualityRating, string> = {
  GREEN: 'Alta',
  YELLOW: 'Media',
  RED: 'Baja',
};

const QUALITY_VARIANTS: Record<WhatsAppQualityRating, BadgeVariant> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'error',
};

// Daily business-initiated conversation limit behind each Meta/360dialog tier.
const TIER_LABELS: Record<WhatsAppMessagingLimitTier, string> = {
  TIER_50: '50 / día',
  TIER_250: '250 / día',
  TIER_1K: '1.000 / día',
  TIER_2K: '2.000 / día',
  TIER_10K: '10.000 / día',
  TIER_100K: '100.000 / día',
  TIER_UNLIMITED: 'Ilimitado',
};

const MasterPhoneCard = ({ project }: Props) => {
  const { data: phoneConfig, isLoading: isLoadingConfig } = useGetProjectPhoneConfigQuery(project.id);

  const [updateProjectPhone, { isLoading: isUpdatingPhone }] = useUpdateProjectPhoneMutation();
  const [updateProject, { isLoading: isReleasing }] = useUpdateProjectMutation();
  const [configureD360Channel, { isLoading: isSaving }] = useConfigureD360ChannelMutation();
  const [configureD360Webhook, { isLoading: isConfiguringWebhook }] = useConfigureD360WebhookMutation();
  const [lookupD360Channel, { isFetching: isLookingUp }] = useLazyLookupD360ChannelQuery();
  const [lookupProjectByPhone, { isFetching: isVerifying }] = useLazyLookupProjectByPhoneQuery();
  const [refreshWhatsAppHealth, { isLoading: isSyncingHealth }] = useRefreshProjectWhatsAppHealthMutation();

  const [draft, setDraft] = useState<Draft>({
    phoneNumber: project.phoneNumber ?? '',
    d360ChannelId: '',
    d360ApiKey: '',
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [verified, setVerified] = useState(false);
  const [conflictProject, setConflictProject] = useState<{ id: number; name: string } | null>(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [releaseConfirmDialogOpen, setReleaseConfirmDialogOpen] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [webhookSuccessOpen, setWebhookSuccessOpen] = useState(false);
  const [updateWebhookPromptOpen, setUpdateWebhookPromptOpen] = useState(false);
  const [webhookErrorOpen, setWebhookErrorOpen] = useState(false);
  const [webhookErrorMessage, setWebhookErrorMessage] = useState<string | null>(null);
  const [allowLetters, setAllowLetters] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);

  useEffect(() => {
    if (phoneConfig !== undefined) {
      const stored = phoneConfig.phoneNumber ?? '';
      setAllowLetters(!!stored && !/^\d+$/.test(stored));
      setDraft({
        phoneNumber: stored,
        d360ChannelId: phoneConfig.whatsAppProperties?.d360ChannelId ?? '',
        d360ApiKey: phoneConfig.whatsAppProperties?.d360ApiKey ?? '',
      });
      setVerified(false);
      setConflictProject(null);
    }
  }, [phoneConfig]);

  if (isLoadingConfig) {
    return (
      <section className="space-y-4 rounded-md border border-border p-6">
        <h2 className="font-display text-lg font-semibold">Datos del número</h2>
        <p className="text-sm text-text-muted">Cargando...</p>
      </section>
    );
  }

  const storedPhone = phoneConfig?.phoneNumber ?? '';
  const hasPhone = !!storedPhone;
  const isPlaceholderPhone = !!storedPhone && !/^\d+$/.test(storedPhone);
  const hasD360 = !!phoneConfig?.whatsAppProperties?.d360ChannelId;
  const origin: 'd360' | 'meta' | null = hasPhone ? (hasD360 || isPlaceholderPhone ? 'd360' : 'meta') : null;
  const webhookActive = hasD360 && phoneConfig?.whatsAppProperties?.webhookActive !== false;
  const qualityRating = phoneConfig?.whatsAppQualityRating ?? null;
  const messagingLimitTier = phoneConfig?.whatsAppMessagingLimitTier ?? null;

  const phoneDirty = draft.phoneNumber.trim() !== (phoneConfig?.phoneNumber ?? '');
  const channelIdDirty = draft.d360ChannelId.trim() !== (phoneConfig?.whatsAppProperties?.d360ChannelId ?? '');
  const apiKeyDirty = draft.d360ApiKey.trim() !== (phoneConfig?.whatsAppProperties?.d360ApiKey ?? '');
  const d360Dirty = channelIdDirty || apiKeyDirty;
  const isDirty = phoneDirty || d360Dirty;

  const useConfigureEndpoint = !allowLetters && (d360Dirty || (phoneDirty && hasD360));

  const { country: phoneCountry } = parsePhoneDigits(draft.phoneNumber);
  const isPhoneValid = isValidPhoneForCountry(draft.phoneNumber, phoneCountry);
  const needsVerify = phoneDirty && !verified;

  const handleFillFields = async () => {
    setLookupError(null);
    const result = await lookupD360Channel(draft.phoneNumber);
    if (!result.data) {
      setLookupError(`No se encontró un canal 360dialog para ${draft.phoneNumber}`);
      return;
    }
    setDraft((prev) => ({
      ...prev,
      d360ChannelId: result.data!.channelId,
      d360ApiKey: result.data!.apiKey,
    }));
  };

  const handleVerify = async () => {
    setLookupError(null);
    setReleaseError(null);
    const result = await lookupProjectByPhone({
      phoneNumber: draft.phoneNumber,
      excludeProjectId: project.id,
    });
    const conflict = result.data?.project ?? null;
    if (!conflict) {
      setVerified(true);
      return;
    }
    setConflictProject(conflict);
    setConflictDialogOpen(true);
  };

  const handleConfirmRelease = async () => {
    if (!conflictProject) return;
    setReleaseError(null);
    const placeholder = `${draft.phoneNumber}_used_by_project_id_${project.id}`;
    try {
      await updateProject({
        id: conflictProject.id,
        patch: { phoneNumber: placeholder, whatsAppProperties: null },
      }).unwrap();
      setReleaseConfirmDialogOpen(false);
      setConflictDialogOpen(false);
      setVerified(true);
    } catch (e) {
      setReleaseError(extractErrorMessage(e, 'No se pudo liberar el número del otro proyecto.'));
    }
  };

  const handleSave = async () => {
    if (!isDirty) return;
    setError(null);

    const savedPhone = draft.phoneNumber.trim();
    const wasConfigureSave = useConfigureEndpoint;

    try {
      if (wasConfigureSave) {
        if (!draft.d360ChannelId.trim() || !draft.d360ApiKey.trim()) {
          setError('Debes completar el Channel ID y el API Key para guardar la configuración.');
          return;
        }
        await configureD360Channel({
          projectId: project.id,
          phoneNumber: savedPhone,
          channelId: draft.d360ChannelId.trim(),
          apiKey: draft.d360ApiKey.trim(),
        }).unwrap();
      } else {
        await updateProjectPhone({
          id: project.id,
          patch: allowLetters
            ? {
                phoneNumber: savedPhone,
                whatsAppProperties: null,
                whatsAppBusinessAccountId: null,
                whatsAppFromPhoneNumberId: null,
                whatsAppToken: null,
              }
            : { phoneNumber: savedPhone },
        }).unwrap();
      }

      setSavedAt(Date.now());
      if (wasConfigureSave) {
        setUpdateWebhookPromptOpen(true);
      }
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar la configuración.'));
    }
  };

  const handleDialogSave = async (phoneNumber: string, channelId: string, apiKey: string) => {
    await configureD360Channel({ projectId: project.id, phoneNumber, channelId, apiKey }).unwrap();
  };

  const handleWebhookConfigure = async () => {
    await configureD360Webhook({ projectId: project.id }).unwrap();
  };

  const handleWebhookEnable = async () => {
    setError(null);
    setWebhookErrorMessage(null);
    try {
      await configureD360Webhook({ projectId: project.id }).unwrap();
      setWebhookErrorOpen(false);
      setWebhookSuccessOpen(true);
    } catch (e) {
      setWebhookErrorMessage(extractErrorMessage(e, 'Error habilitando el webhook, inténtalo de nuevo más tarde.'));
      setWebhookErrorOpen(true);
    }
  };

  const handleHealthSync = async (): Promise<void> => {
    setHealthError(null);
    try {
      const response = await refreshWhatsAppHealth(project.id).unwrap();
      // A 200 with `success: true` only means the project was found; the per-project outcome
      // (and the provider error, when the lookup failed) is in `results`.
      const result = response.results.find((r) => r.projectId === project.id);
      if (result && !result.success) {
        setHealthError(result.error ?? 'No se pudo actualizar la salud del número.');
      }
    } catch (e) {
      setHealthError(extractErrorMessage(e, 'No se pudo actualizar la salud del número.'));
    }
  };

  const isLoading = isUpdatingPhone || isSaving;

  return (
    <section className="space-y-4 rounded-md border border-border p-6">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-lg font-semibold">Datos del número</h2>
        {hasPhone && origin === 'd360' && (
          <div className="flex items-center gap-3">
            {savedAt && !isDirty && <span className="text-xs text-text-muted">Guardado</span>}
            <Button
              type="button"
              onClick={() => {
                void handleSave();
              }}
              disabled={!isDirty || isLoading || needsVerify}
            >
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        )}
      </div>

      {hasPhone && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={qualityRating ? QUALITY_VARIANTS[qualityRating] : 'default'}>
            Calidad: {qualityRating ? QUALITY_LABELS[qualityRating] : 'Sin datos'}
          </Badge>
          <Badge variant={messagingLimitTier ? 'info' : 'default'}>
            Tier: {messagingLimitTier ? TIER_LABELS[messagingLimitTier] : 'Sin datos'}
          </Badge>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            title="Sincronizar calidad y tier con el proveedor"
            aria-label="Sincronizar calidad y tier con el proveedor"
            onClick={() => {
              void handleHealthSync();
            }}
            disabled={isSyncingHealth}
          >
            <RefreshCw className={isSyncingHealth ? 'animate-spin' : undefined} />
          </Button>
          {healthError && <span className="text-xs text-destructive">{healthError}</span>}
        </div>
      )}

      {!hasPhone ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-text-muted">No hay número asociado</p>
          <Button type="button" variant="outline" onClick={() => setDialogOpen(true)}>
            Configurar número
          </Button>
        </div>
      ) : origin === 'meta' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Teléfono</Label>
            <PhoneInput value={phoneConfig?.phoneNumber ?? ''} onChange={() => undefined} disabled />
          </div>
          <div className="space-y-1.5">
            <Label>Origen</Label>
            <div className="flex h-9 items-center gap-3">
              <Badge variant="info">Meta</Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                Configurar número
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor={`phone-number-${project.id}`}>Teléfono</Label>
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-text-muted">
                <Checkbox
                  checked={allowLetters}
                  onCheckedChange={(v) => {
                    setAllowLetters(v === true);
                    setVerified(false);
                  }}
                />
                Permitir letras
              </label>
            </div>
            {allowLetters ? (
              <Input
                id={`phone-number-${project.id}`}
                value={draft.phoneNumber}
                onChange={(e) => {
                  setDraft((prev) => ({ ...prev, phoneNumber: e.target.value }));
                  setVerified(false);
                }}
              />
            ) : (
              <PhoneInput
                inputId={`phone-number-${project.id}`}
                value={draft.phoneNumber}
                onChange={(digits) => {
                  setDraft((prev) => ({ ...prev, phoneNumber: digits }));
                  setVerified(false);
                }}
              />
            )}
            {!allowLetters && phoneDirty && !isPhoneValid && (
              <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                Número de teléfono inválido para {phoneCountry}
              </p>
            )}
            {verified && (
              <p className="flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 p-2 text-sm text-green-800">
                <CheckCircle2 className="h-4 w-4" />
                Número disponible
              </p>
            )}
            {phoneDirty && (
              <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3">
                {needsVerify ? (
                  <>
                    <p className="text-sm text-amber-800">
                      Estás cambiando el número. Verifica disponibilidad para no tener choques con otro proyecto que lo
                      use.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={
                        isVerifying || draft.phoneNumber.trim().length === 0 || (!allowLetters && !isPhoneValid)
                      }
                      onClick={() => {
                        void handleVerify();
                      }}
                    >
                      {isVerifying ? 'Verificando...' : 'Verificar disponibilidad'}
                    </Button>
                  </>
                ) : !allowLetters ? (
                  <>
                    <p className="text-sm text-amber-800">
                      Estás cambiando el número asociado, asegúrate de modificar también los campos asociados.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={isLookingUp || !isPhoneValid}
                      onClick={() => {
                        void handleFillFields();
                      }}
                    >
                      {isLookingUp ? 'Buscando...' : 'Rellenar campos'}
                    </Button>
                  </>
                ) : null}
              </div>
            )}
            {lookupError && (
              <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {lookupError}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Origen</Label>
            <div className="flex h-9 items-center gap-3">
              {isPlaceholderPhone ? (
                <>
                  <Badge variant="default">Sin canal</Badge>
                  <Button type="button" variant="outline" size="sm" onClick={() => setDialogOpen(true)}>
                    Configurar número
                  </Button>
                </>
              ) : (
                <Badge variant="info">360dialog</Badge>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`d360-channel-id-${project.id}`}>Channel ID (360dialog)</Label>
            <Input
              id={`d360-channel-id-${project.id}`}
              value={draft.d360ChannelId}
              onChange={(e) => setDraft((prev) => ({ ...prev, d360ChannelId: e.target.value }))}
              disabled={needsVerify || allowLetters}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`d360-api-key-${project.id}`}>API Key (360dialog)</Label>
            <div className="flex gap-2">
              <Input
                id={`d360-api-key-${project.id}`}
                type={showApiKey ? 'text' : 'password'}
                value={draft.d360ApiKey}
                onChange={(e) => setDraft((prev) => ({ ...prev, d360ApiKey: e.target.value }))}
                className="flex-1"
                disabled={needsVerify || allowLetters}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowApiKey((v) => !v)}
                className="shrink-0"
                disabled={needsVerify || allowLetters}
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Webhook</Label>
            <div className="flex h-9 items-center gap-3">
              {webhookActive ? <Badge variant="success">Activo</Badge> : <Badge variant="default">Deshabilitado</Badge>}
              {hasD360 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void handleWebhookEnable();
                  }}
                  disabled={isConfiguringWebhook || allowLetters}
                >
                  {isConfiguringWebhook
                    ? webhookActive
                      ? 'Actualizando...'
                      : 'Habilitando...'
                    : webhookActive
                      ? 'Actualizar'
                      : 'Habilitar webhook'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}

      <ConfigureNumberDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        projectId={project.id}
        initialPhoneNumber={isPlaceholderPhone ? '' : draft.phoneNumber}
        onSave={handleDialogSave}
        onConfigureWebhook={handleWebhookConfigure}
        isSaving={isSaving}
        isConfiguringWebhook={isConfiguringWebhook}
      />

      <Dialog open={conflictDialogOpen} onOpenChange={setConflictDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Número en uso</DialogTitle>
          </DialogHeader>
          {conflictProject && (
            <p className="text-sm text-text-muted">
              El número <strong>+{draft.phoneNumber}</strong> ya está asignado al proyecto{' '}
              <strong>{conflictProject.name}</strong> (id {conflictProject.id}). Podés liberarlo manualmente (editando
              el otro proyecto) o reasignarlo automáticamente desde acá.
            </p>
          )}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setConflictDialogOpen(false)}>
              Lo modificaré manualmente
            </Button>
            <Button
              onClick={() => {
                setConflictDialogOpen(false);
                setReleaseConfirmDialogOpen(true);
              }}
            >
              Modificar automáticamente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={releaseConfirmDialogOpen} onOpenChange={setReleaseConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar reasignación</DialogTitle>
          </DialogHeader>
          {conflictProject && (
            <div className="space-y-2 text-sm">
              <p className="text-text-muted">Se va a actualizar el proyecto:</p>
              <ul className="space-y-1 rounded-md border border-border bg-muted/30 p-3">
                <li>
                  <span className="text-text-muted">Nombre:</span> <strong>{conflictProject.name}</strong>
                </li>
                <li>
                  <span className="text-text-muted">ID:</span> {conflictProject.id}
                </li>
                <li>
                  <span className="text-text-muted">Número actual:</span> {draft.phoneNumber}
                </li>
                <li className="break-all">
                  <span className="text-text-muted">Nuevo número:</span> {draft.phoneNumber}_used_by_project_id_
                  {project.id}
                </li>
              </ul>
              <p className="text-text-muted">
                Sus credenciales de WhatsApp se limpiarán y el número quedará libre para asignar acá.
              </p>
            </div>
          )}
          {releaseError && (
            <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
              {releaseError}
            </p>
          )}
          <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setReleaseConfirmDialogOpen(false)} disabled={isReleasing}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                void handleConfirmRelease();
              }}
              disabled={isReleasing}
            >
              {isReleasing ? 'Reasignando...' : 'Confirmar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={updateWebhookPromptOpen} onOpenChange={setUpdateWebhookPromptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Información actualizada</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-text-muted">
            La información del número se actualizó exitosamente. ¿Deseas actualizar el webhook?
          </p>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={() => setUpdateWebhookPromptOpen(false)} disabled={isConfiguringWebhook}>
              No
            </Button>
            <Button
              onClick={() => {
                setUpdateWebhookPromptOpen(false);
                void handleWebhookEnable();
              }}
              disabled={isConfiguringWebhook}
            >
              Sí
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={webhookErrorOpen} onOpenChange={setWebhookErrorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Error al habilitar el webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <p className="text-sm text-text-muted">
              El webhook no se pudo habilitar. Verifica que la API Key sea correcta y reintenta, o habilítalo más tarde
              desde la pantalla del proyecto.
            </p>
            {webhookErrorMessage && (
              <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
                {webhookErrorMessage}
              </p>
            )}
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={() => setWebhookErrorOpen(false)} disabled={isConfiguringWebhook}>
              Habilitar luego
            </Button>
            <Button
              onClick={() => {
                void handleWebhookEnable();
              }}
              disabled={isConfiguringWebhook}
            >
              {isConfiguringWebhook ? 'Reintentando...' : 'Reintentar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={webhookSuccessOpen} onOpenChange={setWebhookSuccessOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Webhook habilitado</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-text-muted">El webhook ha sido configurado correctamente.</p>
          <div className="flex justify-end pt-2">
            <Button onClick={() => setWebhookSuccessOpen(false)}>Aceptar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MasterPhoneCard;
